// ─── Medios de pago configurables por negocio (0060) ─────────────────────────
//
// Pedido del piloto: "que no quede Nequi/Daviplata/Transferencia por default;
// que les podamos poner los nombres, añadir o quitar".
//
// Diseño en dos capas:
//   · `tipo` — el CANAL contable, uno del enum payment_method de la DB
//     (CASH/BOLD/NEQUI/DAVIPLATA/TRANSFER). No es visible para el cliente
//     final: decide el COMPORTAMIENTO (CASH activa recibido/cambio y entra al
//     arqueo de efectivo) y mantiene compatibles reportes y ventas viejas.
//   · `nombre` — la etiqueta del negocio ("Bancolombia QR", "Bold", …). Se
//     guarda como SNAPSHOT en cada venta (sales.metodo_nombre): renombrar o
//     borrar un método no reescribe la historia.
//
// El negocio SIN configurar (metodosPago null) usa los 5 de siempre — cero
// cambio para quien no toca nada.

export const LIMITES_METODOS = {
  MAX_METODOS: 12,
  MAX_NOMBRE: 30,
  // Comisión FIJA por transacción (COP): el datáfono del piloto cobra
  // 2,69 % + $300 por venta. Tope generoso; espejo del trigger 0066a.
  MAX_COMISION_FIJA: 5000,
};

// Canales que un método nuevo puede elegir en la UI. DAVIPLATA no se ofrece
// para métodos NUEVOS (es un legacy del default: hoy billetera = NEQUI), pero
// el saneador lo acepta para no romper la lista por defecto.
export const CANALES_PAGO = [
  { tipo: 'CASH',     label: 'Efectivo',            desc: 'Maneja recibido y cambio; entra al arqueo de caja.' },
  { tipo: 'BOLD',     label: 'Datáfono / tarjeta',  desc: 'Pagos con tarjeta o datáfono.' },
  { tipo: 'NEQUI',    label: 'Billetera digital',   desc: 'Nequi, Daviplata y similares.' },
  { tipo: 'TRANSFER', label: 'Transferencia',       desc: 'Consignación o transferencia bancaria.' },
];

const TIPOS_VALIDOS = ['CASH', 'BOLD', 'NEQUI', 'DAVIPLATA', 'TRANSFER'];

// Los 5 de siempre. `fijo` = la UI no deja quitarlo (el POS y el arqueo
// asumen que existe un método de efectivo), aunque sí renombrarlo.
export const METODOS_DEFAULT = [
  { id: 'efectivo', nombre: 'Efectivo',  tipo: 'CASH',      fijo: true },
  { id: 'datafono', nombre: 'Datáfono',  tipo: 'BOLD' },
  { id: 'nequi',    nombre: 'Nequi',     tipo: 'NEQUI' },
  { id: 'daviplata', nombre: 'Daviplata', tipo: 'DAVIPLATA' },
  { id: 'transfer', nombre: 'Transfer.', tipo: 'TRANSFER' },
];

// Espejo del trigger _mezo_sanear_metodos_pago (el servidor es la autoridad;
// esto evita que una config rota en la DB tumbe el POS).
export function sanearMetodosPago(config) {
  const lista = Array.isArray(config?.lista) ? config.lista : null;
  if (!lista) return { lista: METODOS_DEFAULT.map((m) => ({ ...m })) };

  const vistos = new Set();
  const limpia = [];
  for (const e of lista.slice(0, LIMITES_METODOS.MAX_METODOS)) {
    if (!e || typeof e !== 'object') continue;
    const id = String(e.id ?? '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30);
    // Controles + caracteres de formato invisibles (bidi, zero-width):
    // sin esto un nombre con U+202E voltea visualmente el recibo.
    // eslint-disable-next-line no-control-regex
    const nombre = String(e.nombre ?? '').replace(/[\x00-\x1F\x7F\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g, ' ').trim().slice(0, LIMITES_METODOS.MAX_NOMBRE);
    const tipo = String(e.tipo ?? '').toUpperCase();
    if (!id || !nombre || !TIPOS_VALIDOS.includes(tipo) || vistos.has(id)) continue;
    vistos.add(id);
    // Comisión que cobra el datáfono/pasarela por venta: % del monto (0065)
    // + un valor FIJO en COP por transacción (0066a; piloto: 2,69 % + $300).
    const comRaw = Number(String(e.comision_pct ?? e.comisionPct ?? 0).replace(',', '.'));
    const comision_pct = tipo === 'CASH' || !Number.isFinite(comRaw) ? 0 : Math.min(20, Math.max(0, Math.round(comRaw * 100) / 100));
    const comision_fija = tipo === 'CASH' ? 0 : sanearComisionFija(e.comision_fija ?? e.comisionFija);
    limpia.push({ id, nombre, tipo, comision_pct, comision_fija, ...(tipo === 'CASH' && id === 'efectivo' ? { fijo: true } : {}) });
  }
  if (limpia.length === 0) return { lista: METODOS_DEFAULT.map((m) => ({ ...m })) };
  // El POS y el arqueo asumen que existe un método de caja: si la lista quedó
  // sin CASH (p.ej. borraron el nombre de Efectivo para renombrarlo y
  // guardaron), se restaura el de mezo al frente. Espejo del trigger 0060b.
  if (!limpia.some((m) => m.tipo === 'CASH')) {
    limpia.unshift({ id: 'efectivo', nombre: 'Efectivo', tipo: 'CASH', comision_pct: 0, comision_fija: 0, fijo: true });
    limpia.length = Math.min(limpia.length, LIMITES_METODOS.MAX_METODOS);
  }
  return { lista: limpia };
}

// Entero COP 0..5000. Acepta número o texto tal como lo escribe la gente:
// "300", "1.000" (punto de miles) o "1 000". La coma se toma como decimal
// (convención colombiana) y se redondea: la comisión fija no lleva centavos.
function sanearComisionFija(valor) {
  const n = typeof valor === 'number'
    ? valor
    : Number(String(valor ?? 0).replace(/[.\s]/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) return 0;
  return Math.min(LIMITES_METODOS.MAX_COMISION_FIJA, Math.max(0, Math.round(n)));
}

// Resuelve QUÉ medio configurado aplica a una venta: el del nombre exacto
// (snapshot sales.metodo_nombre) o, si no hay, el del canal `tipo` con la
// comisión más alta (CARD ≡ BOLD: así llega el datáfono desde ventas viejas).
// Devuelve { comision_pct, comision_fija } — nunca null.
function medioQueAplica(negocioOMetodos, { tipo, nombre } = {}) {
  const lista = Array.isArray(negocioOMetodos?.lista)
    ? sanearMetodosPago(negocioOMetodos).lista
    : metodosDelNegocio(negocioOMetodos);
  const porNombre = nombre ? lista.find((m) => m.nombre === nombre) : null;
  if (porNombre) return porNombre;
  const t = tipo === 'CARD' ? 'BOLD' : tipo;
  return lista.filter((m) => m.tipo === t).reduce((mejor, m) => {
    // "Mayor" = el que más cobra sobre una venta típica: primero por %, y a
    // igual % gana el fijo más alto — así no se subestima la comisión.
    const pM = Number(m.comision_pct) || 0, pB = Number(mejor.comision_pct) || 0;
    if (pM > pB) return m;
    if (pM === pB && (Number(m.comision_fija) || 0) > (Number(mejor.comision_fija) || 0)) return m;
    return mejor;
  }, { comision_pct: 0, comision_fija: 0 });
}

// Comisión (%) que aplica a una venta cobrada por `tipo` (canal) o por el
// nombre custom del medio. Sin config → 0. Se mantiene por compatibilidad;
// para plata usa `comisionDe`, que ya suma el fijo por transacción.
export function comisionPct(negocioOMetodos, ref = {}) {
  return Number(medioQueAplica(negocioOMetodos, ref).comision_pct) || 0;
}

// Valor fijo (COP) por transacción del medio que aplica. Simétrica a comisionPct.
export function comisionFija(negocioOMetodos, ref = {}) {
  return Number(medioQueAplica(negocioOMetodos, ref).comision_fija) || 0;
}

// Comisión en COP de UNA transacción: monto × % / 100 + fijo. Con monto 0 (o
// negativo) no hay transacción que cobrar → 0, así una orden anulada no suma
// $300 fantasma. No redondea: quien suma varias ventas redondea al final.
export function comisionDe(negocioOMetodos, { tipo, nombre, monto } = {}) {
  const m = Number(monto) || 0;
  if (m <= 0) return 0;
  const medio = medioQueAplica(negocioOMetodos, { tipo, nombre });
  return m * (Number(medio.comision_pct) || 0) / 100 + (Number(medio.comision_fija) || 0);
}

// Lista lista para el POS a partir de lo que traiga el negocio.
export function metodosDelNegocio(negocio) {
  return sanearMetodosPago(negocio?.metodosPago).lista;
}

// Emoji por canal — para los botones del POS y la división de cuenta.
export function emojiDeTipo(tipo) {
  return { CASH: '💵', BOLD: '💳', NEQUI: '📱', DAVIPLATA: '🔵', TRANSFER: '🏦' }[tipo] ?? '💠';
}
